import React, { useEffect, useState } from 'react';
import { fetchExhibitionNominations } from '../api'; 

const ExhibitionsTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // --- FILTER STATES ---
  const [searchTerm, setSearchTerm] = useState(''); 
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedState, setSelectedState] = useState('');

  // ---------------------------------------------------------------------------
  // DYNAMIC IMAGE URL CONFIGURATION
  // ---------------------------------------------------------------------------
  const isLocal = window.location.hostname === "localhost";
  const IMG_BASE_URL = isLocal 
    ? "http://localhost:5279/Uploads/"           
    : "https://dmsserver.nsfdcdigital.in/Uploads/"; 

  const API_BASE_URL = isLocal
    ? "http://localhost:5279/api"
    : "https://dmsserver.nsfdcdigital.in/api";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    fetchExhibitionNominations()
      .then(res => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching nominations:", err);
        setIsLoading(false);
      });
  };

  const deleteApplication = async (id) => {
    const email = prompt("Enter Super Admin Email:");
    if (email === null) return; 
    
    const password = prompt("Enter Super Admin Password:");
    if (password === null) return; 

    if (email.trim().toLowerCase() !== "nsfdc.it@gmail.com") {
        alert("Only the Super Admin can delete records.");
        return;
    }

    if (!window.confirm(`Are you absolutely sure you want to permanently delete Application Record #${id}?`)) {
        return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/superadmin/nomination/${id}`, {
            method: 'DELETE',
            headers: {
                'x-admin-user': email.trim(),
                'x-admin-pass': password
            }
        });

        const result = await response.json();
        
        if (response.ok) {
            alert(result.message || "Record successfully deleted.");
            loadData(); 
        } else {
            alert(result.message || "Failed to delete resource.");
        }
    } catch (err) {
        alert("Server network error. Could not reach the Super Admin endpoint.");
    }
  };

  const uniqueStates = [...new Set(data.map(item => item.state))].filter(Boolean).sort();

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedState('');
  };

  const filteredData = data.filter(item => {
    const searchString = `
      ${item.applicationId} ${item.name} ${item.artisanId} ${item.mobile} ${item.aadhaarNumber} ${item.district} 
      ${item.state} ${item.productDetails} ${item.productDescription} ${item.melaName1} 
      ${item.melaName2} ${item.melaName3} ${item.clusterName}
    `.toLowerCase();
    const matchesSearch = searchString.includes(searchTerm.toLowerCase());

    const matchesState = selectedState === '' || item.state === selectedState;

    let matchesDate = true;
    if (startDate || endDate) {
      if (!item.submissionDate) {
        matchesDate = false; 
      } else {
        const itemDate = new Date(item.submissionDate);
        itemDate.setHours(0, 0, 0, 0); 

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (itemDate < start) matchesDate = false;
        }
        
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); 
          if (itemDate > end) matchesDate = false;
        }
      }
    }

    return matchesSearch && matchesState && matchesDate;
  });

  const handleExportExcel = () => {
    const headers = [
      "ID", "Application ID", "Has Artisan Card", "Artisan ID", "Name", "Father/Husband Name", "Gender", "Age", "Category", 
      "Belongs to Cluster/NGO/SHG etc.", "Cluster/NGO/SHG etc. Name", "Cluster Artisans", 
      "Mobile", "Aadhaar Number", "Address", "District", "State", "Pin Code", 
      "Family Income", "NSFDC Loan Availed", "Loan Amount", "Loan Sanction Date",
      "Product Category", "Product Description", "First Time Participant", "Total Previous Participations",
      "Participation 1 Name", "Participation 1 Date", "Participation 2 Name", "Participation 2 Date", "Participation 3 Name", "Participation 3 Date",
      "Agreed to Conduct", "Product Photo 1 URL", "Product Photo 2 URL", "Product Photo 3 URL",
      "Artisan Card URL", "SC Certificate URL", "Income Certificate URL", "Aadhaar/Voter ID Document URL", "Loan Proof URL", "Submission Date"
    ];

    const csvRows = filteredData.map((item, index) => {
      const photos = item.productPhotosPaths ? item.productPhotosPaths.split(',') : [];
      const escapeCsv = (text) => `"${(text || "").toString().replace(/"/g, '""')}"`;
      
      const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : "";
      
      const formatMonthYear = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return ""; 
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${month}/${year}`;
      };

      return [
        index + 1,
        item.applicationId || "", 
        item.hasArtisanCard || "", 
        item.artisanId || "",
        item.name || "",
        item.fatherHusbandName || "",
        item.gender || "",
        item.age || "",
        item.category || "",
        
        item.belongsToCluster || "",
        escapeCsv(item.clusterName),
        item.clusterArtisanCount || "",

        item.mobile || "",
        item.aadhaarNumber || "",
        escapeCsv(item.address),
        item.district || "",
        item.state || "",
        item.pinCode || "",
        item.familyIncome || "",
        item.nsfdcLoanAvailed || "",
        item.loanSanctionAmount || "",
        formatDate(item.loanSanctionDate),
        
        item.productDetails || "",
        escapeCsv(item.productDescription),
        item.isFirstTime || "",
        item.previousParticipationsCount || "",
        
        escapeCsv(item.melaName1), formatMonthYear(item.melaDate1),
        escapeCsv(item.melaName2), formatMonthYear(item.melaDate2),
        escapeCsv(item.melaName3), formatMonthYear(item.melaDate3),
        
        item.agreedToConduct ? "Yes" : "No",
        photos[0] ? `${IMG_BASE_URL}${photos[0]}` : "",
        photos[1] ? `${IMG_BASE_URL}${photos[1]}` : "",
        photos[2] ? `${IMG_BASE_URL}${photos[2]}` : "",
        item.artisanCardPath ? `${IMG_BASE_URL}${item.artisanCardPath}` : "",
        item.scCertificatePath ? `${IMG_BASE_URL}${item.scCertificatePath}` : "",
        item.incomeCertificatePath ? `${IMG_BASE_URL}${item.incomeCertificatePath}` : "",
        item.aadhaarVoterIdPath ? `${IMG_BASE_URL}${item.aadhaarVoterIdPath}` : "",
        item.proofOfLoanPath ? `${IMG_BASE_URL}${item.proofOfLoanPath}` : "",
        item.submissionDate ? new Date(item.submissionDate).toLocaleString() : ""
      ].join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Exhibition_Nominations_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container-fluid mt-4 px-4">
      {/* Top Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="text-black fw-bold mb-0">Tulip/Exhibition Registrations</h4>
          <span className="badge bg-secondary fs-6">{filteredData.length} Records Found</span>
        </div>
        <button className="btn btn-sm btn-success d-flex align-items-center fw-bold shadow-sm" onClick={handleExportExcel}>
          <i className="bi bi-file-earmark-excel me-2"></i> Export Filtered to Excel
        </button>
      </div>

      {/* FILTER TOOLBAR */}
      <div className="bg-light p-3 rounded border mb-3 shadow-sm">
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label small fw-bold text-muted mb-1">Search Keywords</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
              <input 
                type="text" 
                className="form-control" 
                placeholder="App ID, Name, Phone..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">Filter by State</label>
            <select 
              className="form-select form-select-sm" 
              value={selectedState} 
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">All States</option>
              {uniqueStates.map((state, idx) => (
                <option key={idx} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">From Date</label>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">To Date</label>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={endDate}
              min={startDate} 
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-2 d-grid">
            <button className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>
              <i className="bi bi-x-circle me-1"></i> Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted">Loading Nominations...</p>
        </div>
      ) : (
        <div className="table-responsive shadow-sm rounded bg-white" style={{ minHeight: '500px' }}>
          <table className="table table-hover table-bordered align-middle mb-0" style={{ fontSize: '0.85rem' }}>
            <thead className="table-dark text-center align-middle sticky-top">
              <tr>
                <th style={{width: '50px'}}>#</th>
                <th style={{minWidth: '260px'}}>Application & Artisan</th>
                <th style={{minWidth: '240px'}}>Location & Finances</th>
                <th style={{minWidth: '280px'}}>Product Details</th>
                <th style={{minWidth: '240px'}}>Participation History</th>
                <th style={{minWidth: '180px'}}>Documents & Actions</th>
                <th style={{minWidth: '100px'}}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-center fw-bold bg-light">{index + 1}</td>
                  
                  {/* 1. Application & Artisan Details */}
                  <td>
                    <div className="mb-2 p-1 bg-warning bg-opacity-10 border border-warning rounded text-center">
                      <small className="text-muted d-block" style={{fontSize: '0.7rem', textTransform: 'uppercase'}}>Registration ID</small>
                      <span className="font-monospace text-dark fw-bold fs-6">{item.applicationId || 'Pending'}</span>
                    </div>

                    <div className="mb-1">
                      <strong>Artisan Card:</strong>{' '}
                      {item.hasArtisanCard === "Yes" ? (
                        <span className="text-success fw-bold">Yes ({item.artisanId})</span>
                      ) : (
                        <span className="text-muted">No</span>
                      )}
                    </div>

                    <div className="mb-1"><strong>Full Name:</strong> {item.name}</div>
                    <div className="mb-1"><strong>Father's/Husband's:</strong> {item.fatherHusbandName}</div>
                    <div className="mb-1"><strong>Age:</strong> {item.age} Years</div>
                    <div className="mb-1"><strong>Gender:</strong> {item.gender}</div>
                    <div className="mb-1"><strong>Category:</strong> <span className="badge bg-danger">{item.category}</span></div>
                    <div className="mb-1"><strong>Mobile No:</strong> {item.mobile}</div>
                    <div className="mb-2"><strong>Aadhaar No:</strong> <span className="font-monospace">{item.aadhaarNumber || 'N/A'}</span></div>
                    
                    <div className="mt-2 pt-2 border-top">
                      <div className="mb-1">
                        <strong>Cluster/NGO/SHG etc. Member:</strong> {item.belongsToCluster === 'Yes' ? <span className="text-success fw-bold">Yes</span> : 'No'}
                      </div>
                      {item.belongsToCluster === 'Yes' && (
                        <div className="bg-light p-2 rounded border border-info border-opacity-25 mt-1 text-break">
                          <div className="mb-1"><strong>Name:</strong> {item.clusterName}</div>
                          <div><strong>Total Artisans:</strong> {item.clusterArtisanCount}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  {/* 2. Location & Finances */}
                  <td>
                    <div className="mb-1"><strong>State:</strong> {item.state}</div>
                    <div className="mb-1"><strong>District:</strong> {item.district}</div>
                    <div className="mb-1"><strong>Pin Code:</strong> {item.pinCode}</div>
                    <div className="mb-2">
                      <strong>Complete Address:</strong><br/>
                      <span className="text-muted fst-italic">{item.address}</span>
                    </div>
                    <hr className="my-2 text-muted opacity-25" />
                    <div className="mb-1"><strong>Annual Family Income:</strong> ₹{item.familyIncome?.toLocaleString() || '0'}</div>
                    <div className="mb-1">
                      <strong>NSFDC Loan Availed:</strong> {item.nsfdcLoanAvailed === 'Yes' ? <span className="text-success fw-bold">Yes</span> : 'No'}
                    </div>

                    {item.nsfdcLoanAvailed === 'Yes' && (
                      <>
                        <div className="mb-1"><strong>Loan Sanction Amount:</strong> ₹{item.loanSanctionAmount?.toLocaleString() || '0'}</div>
                        <div>
                          <strong>Loan Sanction Date:</strong>{' '}
                          {item.loanSanctionDate ? new Date(item.loanSanctionDate).toLocaleDateString() : <span className="text-muted">N/A</span>}
                        </div>
                      </>
                    )}
                  </td>

                  {/* 3. Product Info & Photos */}
                  <td>
                    <div className="mb-2"><strong>Product Category:</strong> {item.productDetails}</div>
                    <div className="mb-2">
                      <strong>Product Description:</strong><br/>
                      <div className="text-muted fst-italic border-start border-2 border-secondary ps-2 mt-1" style={{maxHeight: '60px', overflowY: 'auto'}}>
                        {item.productDescription}
                      </div>
                    </div>
                    
                    <div className="d-flex flex-column gap-2 bg-light p-2 rounded border mt-3">
                      <div className="fw-bold mb-1 border-bottom pb-1">Product Photos:</div>
                      {item.productPhotosPaths ? (
                        item.productPhotosPaths.split(',').map((path, idx) => (
                          <div key={idx} className="d-flex align-items-center gap-2">
                            <a href={`${IMG_BASE_URL}${path}`} target="_blank" rel="noopener noreferrer">
                              <img 
                                src={`${IMG_BASE_URL}${path}`} 
                                alt={`Thumb ${idx + 1}`}
                                loading="lazy" /* CRITICAL FIX: LAZY LOADING */
                                className="rounded shadow-sm bg-white"
                                style={{ width: '35px', height: '35px', objectFit: 'cover', border: '1px solid #dee2e6' }}
                                onError={(e) => {e.target.style.display = 'none'}}
                              />
                            </a>
                            <a 
                              href={`${IMG_BASE_URL}${path}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary text-truncate"
                              style={{ maxWidth: '190px' }}
                            >
                              <i className="bi bi-link-45deg"></i> View Image {idx + 1}
                            </a>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted py-1 w-100">No Photos</span>
                      )}
                    </div>
                  </td>

                  {/* 4. Participation History */}
                  <td>
                    <div className="mb-2">
                      <strong>First Time Participant:</strong> {item.isFirstTime === 'Yes' ? <span className="text-success fw-bold">Yes</span> : 'No'}
                    </div>

                    {item.isFirstTime === 'No' && (
                      <div className="bg-light border rounded p-2 mb-2">
                        <div className="mb-2">
                          <strong>Total Previous Participations:</strong> <span className="badge bg-primary">{item.previousParticipationsCount}</span>
                        </div>
                        
                        {item.melaName1 && (
                          <div className="mb-1 border-top pt-1">
                            <strong>1. {item.melaName1}</strong>
                            {item.melaDate1 && (
                              <div className="text-muted small">
                                <i className="bi bi-calendar me-1"></i> 
                                {`${String(new Date(item.melaDate1).getMonth() + 1).padStart(2, '0')}/${new Date(item.melaDate1).getFullYear()}`}
                              </div>
                            )}
                          </div>
                        )}
                        {item.melaName2 && (
                          <div className="mb-1 border-top pt-1">
                            <strong>2. {item.melaName2}</strong>
                            {item.melaDate2 && (
                              <div className="text-muted small">
                                <i className="bi bi-calendar me-1"></i> 
                                {`${String(new Date(item.melaDate2).getMonth() + 1).padStart(2, '0')}/${new Date(item.melaDate2).getFullYear()}`}
                              </div>
                            )}
                          </div>
                        )}
                        {item.melaName3 && (
                          <div className="mb-1 border-top pt-1">
                            <strong>3. {item.melaName3}</strong>
                            {item.melaDate3 && (
                              <div className="text-muted small">
                                <i className="bi bi-calendar me-1"></i> 
                                {`${String(new Date(item.melaDate3).getMonth() + 1).padStart(2, '0')}/${new Date(item.melaDate3).getFullYear()}`}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-2 pt-2 border-top">
                      <strong>Agreed to Code of Conduct:</strong> {item.agreedToConduct ? <span className="text-success fw-bold">Yes</span> : <span className="text-danger fw-bold">No</span>}
                    </div>
                  </td>

                  {/* 5. Documents & Actions (CRITICAL FIX: NATIVE LINKS) */}
                  <td>
                    <div className="d-grid gap-1">
                      {item.artisanCardPath ? (
                        <a href={`${IMG_BASE_URL}${item.artisanCardPath}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 text-start">
                          <i className="bi bi-card-heading me-1"></i> Artisan Card
                        </a>
                      ) : <span className="text-muted ms-1" style={{fontSize: '0.8rem'}}>- No Artisan Card</span>}

                      {item.scCertificatePath ? (
                        <a href={`${IMG_BASE_URL}${item.scCertificatePath}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 text-start mt-1">
                          <i className="bi bi-file-earmark-person me-1"></i> SC Cert
                        </a>
                      ) : <span className="text-danger ms-1" style={{fontSize: '0.8rem'}}>- Missing SC Cert</span>}

                      {item.incomeCertificatePath ? (
                        <a href={`${IMG_BASE_URL}${item.incomeCertificatePath}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 text-start mt-1">
                          <i className="bi bi-cash-coin me-1"></i> Income Cert
                        </a>
                      ) : <span className="text-danger ms-1" style={{fontSize: '0.8rem'}}>- Missing Income Cert</span>}

                      {item.aadhaarVoterIdPath ? (
                        <a href={`${IMG_BASE_URL}${item.aadhaarVoterIdPath}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 text-start mt-1">
                          <i className="bi bi-person-vcard me-1"></i> Aadhaar Doc
                        </a>
                      ) : <span className="text-danger ms-1" style={{fontSize: '0.8rem'}}>- Missing ID Doc</span>}

                      {item.proofOfLoanPath && (
                        <a href={`${IMG_BASE_URL}${item.proofOfLoanPath}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success py-0 text-start mt-2 border-success">
                          <i className="bi bi-bank me-1"></i> Loan Proof
                        </a>
                      )}

                      <div className="mt-3 pt-2 border-top border-secondary border-opacity-25 text-center">
                        <button 
                          className="btn btn-xs btn-danger fw-bold w-100 py-1" 
                          style={{fontSize: '0.75rem'}}
                          onClick={() => deleteApplication(item.id)}
                        >
                          <i className="bi bi-trash3-fill me-1"></i> Delete Record
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* 6. Submission Date */}
                  <td className="text-center bg-light">
                    <div className="fw-bold text-dark">{item.submissionDate ? new Date(item.submissionDate).toLocaleDateString() : '-'}</div>
                    <div className="text-muted">{item.submissionDate ? new Date(item.submissionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</div>
                  </td>
                </tr>
              ))}
              
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted bg-light border-0 mt-3 rounded">
                    <i className="bi bi-funnel fs-1 d-block mb-3 text-secondary"></i>
                    <h5>No Records Match Your Filters</h5>
                    <p>Try adjusting your search terms, state, or date range.</p>
                    <button className="btn btn-outline-primary mt-2" onClick={resetFilters}>Clear All Filters</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExhibitionsTable;